// components/Tooltip.jsx
import React, { useState, useRef } from "react";
import {
	useFloating,
	autoUpdate,
	offset,
	flip,
	shift,
	useHover,
	useFocus,
	useDismiss,
	useRole,
	useInteractions,
	FloatingPortal,
	arrow,
	safePolygon,
} from "@floating-ui/react";
import "../styling/Tooltip.css";

export default function Tooltip({
	children,
	content,
	placement = "top",
  	delay = { open: 150, close: 0 },
}) {
	const [isOpen, setIsOpen] = useState(false);
	const arrowRef = useRef(null);

	const { refs, floatingStyles, context, middlewareData } = useFloating({
		open: isOpen,
		onOpenChange: setIsOpen,
		placement,
		whileElementsMounted: autoUpdate,
		middleware: [
			offset(8),
			flip({ fallbackPlacements: ["bottom", "left", "right"] }),
			shift({ padding: 8 }),
			arrow({ element: arrowRef }),
		],
	});

	const hover = useHover(context, { delay, handleClose: safePolygon() });
	const focus = useFocus(context);
	const dismiss = useDismiss(context);
	const role = useRole(context, { role: "tooltip" });

	const { getReferenceProps, getFloatingProps } = useInteractions([
		hover, focus, dismiss, role,
	]);

	const side = context.placement?.split("-")[0] || "top";
	const arrowSide = { top: "bottom", bottom: "top", left: "right", right: "left" }[side];

	return (
		<>
			<span ref={refs.setReference} {...getReferenceProps()} className="tooltip-trigger">
				{children}
			</span>
			{isOpen && content && (
				<FloatingPortal>
					<div
						ref={refs.setFloating}
						style={floatingStyles}
						{...getFloatingProps()}
						className="tooltip-box"
					>
						{content}
						<div
							ref={arrowRef}
							className="tooltip-arrow"
							style={{
								left: middlewareData.arrow?.x ?? "",
								top: middlewareData.arrow?.y ?? "",
								[arrowSide]: -4,
							}}
						/>
					</div>
				</FloatingPortal>
			)}
		</>
	);
}

export function HelpTip({ text }) {
	if (!text) return null;
	return (
		<Tooltip content={text}>
			<span className="help-icon" aria-label="More info">?</span>
		</Tooltip>
	);
}